import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Alert,
  CircularProgress,
  IconButton,
  Typography,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../services/api';

const REPORT_TYPES = [
  { value: 'wrong_answer', label: 'Wrong answer' },
  { value: 'unclear_question', label: 'Unclear question' },
  { value: 'formatting_error', label: 'Formatting error' },
  { value: 'inappropriate_content', label: 'Inappropriate content' },
  { value: 'other', label: 'Other' }
];

const ReportContentDialog = ({ open, onClose, contentItem, contentType }) => {
  const [reportType, setReportType] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleClose = () => {
    if (submitting) return;
    setReportType('');
    setDescription('');
    setError('');
    setSuccess(false);
    onClose(success);
  };

  const handleSubmit = async () => {
    if (!reportType) {
      setError('Please select a report type.');
      return;
    }
    if (!description.trim()) {
      setError('Please describe the issue.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const idField = contentType === 'worked-example' ? 'workedExampleId' : 'questionId';
      const body = {
        [idField]: contentItem._id,
        reportType,
        description: description.trim()
      };
      await api.post('/api/content-reports', body);
      setSuccess(true);
    } catch (err) {
      const msg = err.response?.data?.message
        || err.response?.data?.error
        || 'Failed to submit report. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const label = contentType === 'worked-example' ? 'worked example' : 'question';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6" component="span" sx={{ fontSize: '1.05rem', fontWeight: 600 }}>
          Report an issue
        </Typography>
        <IconButton size="small" onClick={handleClose} disabled={submitting} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {success ? (
          <Alert severity="success" sx={{ mb: 1 }}>
            Thank you! Your report has been submitted and our team will review it.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Help us improve by reporting issues with this {label}.
              {contentItem.topic && (
                <Box component="span" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                  Grade {contentItem.grade} &bull; {contentItem.topic}
                  {contentItem.subtopic ? ` — ${contentItem.subtopic}` : ''}
                </Box>
              )}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 1, fontWeight: 500, fontSize: '0.9rem' }}>
                What is the issue?
              </FormLabel>
              <RadioGroup value={reportType} onChange={(e) => setReportType(e.target.value)}>
                {REPORT_TYPES.map((rt) => (
                  <FormControlLabel
                    key={rt.value}
                    value={rt.value}
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">{rt.label}</Typography>}
                    sx={{ mb: -0.5 }}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <TextField
              label="Describe the issue"
              placeholder="e.g. The correct answer should be 42, not 24..."
              multiline
              rows={3}
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
              inputProps={{ maxLength: 1000 }}
              helperText={`${description.length}/1000`}
              sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem' } }}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {success ? (
          <Button onClick={handleClose} variant="contained" size="small">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting} size="small">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={submitting || !reportType || !description.trim()}
              size="small"
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {submitting ? 'Submitting...' : 'Submit Report'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReportContentDialog;
